import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery, useTheme } from '@material-ui/core';
import { ReportCardComponentProps } from '../schema-types';
import { filterDOMProps } from '../utils/prop-normalizer';
import { substituteVariables } from './text-utils';

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

interface TableData {
  title?: string;
  columns: TableColumn[];
  rows: { [key: string]: any }[];
  backgroundColor?: string;
  headerColor?: string;
  borderRadius?: number;
  showBorder?: boolean;
}

export const TableComponent: React.FC<ReportCardComponentProps> = ({
  baseData,
  data,
  player,
  playerAssessment,
  reportCardLevel: _reportCardLevel,
  context,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const {
    title,
    columns = [],
    rows = [],
    backgroundColor = '#f9f9f9',
    headerColor = '#8bc34a',
    borderRadius = 8,
    showBorder = true
  }: TableData = {...baseData, ...data};

  // Responsive sizing
  const tableSize = isMobile ? 'small' : 'medium';
  const cellPadding = isMobile ? '4px 8px' : '8px 16px';
  const fontSize = isMobile ? '0.75rem' : isTablet ? '0.875rem' : '1rem';

  return (
    <Box mb={3} {...filterDOMProps(props)}>
      {title && (
        <Box
          mb={2}
          p={1}
          style={{
            backgroundColor: headerColor,
            borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
            display: 'inline-block',
            minWidth: '200px'
          }}
        >
          <Typography
            variant="subtitle1"
            style={{
              color: 'white',
              fontWeight: 'bold',
              textAlign: 'center'
            }}
          >
            {substituteVariables(title, data, baseData, player, playerAssessment)}
          </Typography>
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={showBorder ? 2 : 0}
        style={{
          borderRadius: title ? `0 ${borderRadius}px ${borderRadius}px ${borderRadius}px` : borderRadius,
          backgroundColor,
          // Enable horizontal scrolling on mobile
          overflowX: isMobile ? 'auto' : 'visible',
          // Maintain aspect ratios with responsive sizing
          maxWidth: '100%',
          width: '100%'
        }}
      >
        <Table
          size={tableSize}
          style={{
            minWidth: isMobile ? '100%' : 'auto',
            // Prevent table from being too narrow on mobile
            width: isMobile && columns.length > 3 ? '120%' : '100%'
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  align={column.align || 'left'}
                  style={{
                    width: column.width,
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderBottom: '2px solid #ddd',
                    padding: cellPadding,
                    fontSize,
                    // Prevent cell content from wrapping on mobile for readability
                    whiteSpace: isMobile ? 'nowrap' : 'normal',
                    minWidth: isMobile ? '80px' : 'auto'
                  }}
                >
                  {substituteVariables(column.label, data, baseData, player, playerAssessment)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={column.align || 'left'}
                    style={{
                      borderBottom: rowIndex === rows.length - 1 ? 'none' : '1px solid #eee',
                      padding: cellPadding,
                      fontSize,
                      whiteSpace: isMobile ? 'nowrap' : 'normal',
                      minWidth: isMobile ? '80px' : 'auto'
                    }}
                  >
                    {row[column.key] || '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
